import ArticleTranscriptGenerationStatus from "src/types/article-transcript-generation-status";
import FileSaver from "file-saver";
import { IDiffExtendedItemWithGroup, IPron, IQaPron } from "./types";
import ArticleNarrationTypes from "src/types/article-narration-types";
import IArticle from "src/types/article";
import _ from "lodash";

export const isTranscriptAvailable = (transcriptStatus?: ArticleTranscriptGenerationStatus) => {
    if (transcriptStatus === ArticleTranscriptGenerationStatus.SUCCESS) return true;

    return false;
};

export const isTranscriptFailed = (transcriptStatus?: ArticleTranscriptGenerationStatus) => {
    if (transcriptStatus === ArticleTranscriptGenerationStatus.FAILURE) return true;

    return false;
};

export const isTranscriptPending = (transcriptStatus?: ArticleTranscriptGenerationStatus) => {
    if (transcriptStatus === ArticleTranscriptGenerationStatus.PENDING) return true;

    return false;
};

export function mergeDiffItemsWithByGroupId<T extends IDiffExtendedItemWithGroup>(diff: T[]): T[] {
    return diff.reduce((prev, curr) => {
        const groupItemIndex = prev.findIndex((item) => !!curr.groupId && item.groupId === curr.groupId);

        const prevCloned = [...prev];

        if (groupItemIndex === -1) {
            return [...prevCloned, curr];
        }

        const item = { ...prevCloned[groupItemIndex] };

        if (curr.type === "INSERTION") {
            item.hypothesis = `${item.hypothesis} ${curr.hypothesis}`;
        } else if (curr.type === "DELETION") {
            item.reference = `${item.reference} ${curr.reference}`;
        }

        prevCloned[groupItemIndex] = item;

        return prevCloned;
    }, [] as T[]);
}

export const truncateTheWordsText = (text: string, maxLength?: number): string => {
    const mLength = maxLength || 34;
    if (text.length <= mLength) {
        return text;
    }

    const words = text.split(" ");

    let startWords = "";
    let endWords = "";

    for (let i = 0; i < words.length; i++) {
        const currWord = words[i];
        if ((startWords + currWord).length <= mLength / 2) {
            startWords = `${startWords} ${currWord}`;
        }
    }

    for (let i = words.length - 1; i >= 0; i--) {
        const currWord = words[i];
        if ((endWords + currWord).length <= mLength / 2) {
            endWords = `${endWords} ${currWord}`;
        }
    }

    return `${startWords} ... ${endWords}`;
};

export const qaIssuesCount = (diff: IDiffExtendedItemWithGroup[]): { insertionsCount: number; deletionsCount: number; substitutionsCount: number } => {
    return diff.reduce(
        (prev, curr) => {
            if (curr.type === "INSERTION") return { ...prev, insertionsCount: prev.insertionsCount + 1 };
            if (curr.type === "DELETION") return { ...prev, deletionsCount: prev.deletionsCount + 1 };
            if (curr.type === "SUBSTITUTION") return { ...prev, substitutionsCount: prev.substitutionsCount + 1 };

            return prev;
        },
        { insertionsCount: 0, deletionsCount: 0, substitutionsCount: 0 },
    );
};

export const jsonToCsv = (items: Array<{ [key: string]: string | number | null | undefined }>) => {
    const header = Object.keys(items[0]);
    const headerString = header.join(",");

    const replacer = (key: any, value: any) => value ?? "";

    const rowItems = items.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(","));

    const csv = [headerString, ...rowItems].join("\r\n");
    return csv;
};

export const exportCsvReport = (title: string, text: string) => {
    const csvData = new Blob([text], { type: "text/csv;charset=utf-8;" });

    FileSaver.saveAs(csvData, `${title}.csv`);
};

export const formatTime = (milliseconds: number) => {
    const seconds = Math.floor((milliseconds / 1000) % 60);
    const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
    const hours = Math.floor((milliseconds / 1000 / 60 / 60) % 24);

    return [hours.toString().padStart(2, "0"), minutes.toString().padStart(2, "0"), seconds.toString().padStart(2, "0")].join(":");
};

export const collectArticleTextForPronunciationsQa = (diff: IDiffExtendedItemWithGroup[], getDiffId: (id: string, word: string) => string) => {
    return diff
        .reduce((joined, item) => {
            const itemType = item.type;

            if (itemType === "INSERTION" || itemType === "LINEBREAK") return joined;

            // making it lowercase important because the matching words will be made lowercased too.
            // also normalize it too
            const referenceWord = item.reference?.replaceAll("'s", "");

            if (!referenceWord) return joined;

            const diffId = ` ${getDiffId(item.id, referenceWord)}`;
            return `${joined} ${referenceWord}${diffId}`;
        }, "")
        .trim();
};

export const collectArticleTextForPronsQaAndAddDiffId = (diff: IDiffExtendedItemWithGroup[]) => {
    /* 
        adding the diff ids to the individual words, so that we can get them back after the prons match\
        and then get diff items using the diff ids for getting the information like startTime etc.
    */

    //! CHANGE THESE VALUES WITH CARE
    //! DIFF IDs must be only chars, numbers and dashes(-). SEE REGEX BELOW.

    const DIFF_ID_PREFIX = "//DIFFID";
    const DIFF_ID_POSTFIX = "DIFFID//";

    // ${word} before and after makes the match more specific and it won't make the \b in regex invalid.
    const diffIdRegexStr = (word: string) => `${word}${DIFF_ID_PREFIX}[-A-Za-z0-9]+${DIFF_ID_POSTFIX}${word}`;
    const getDiffId = (id: string, word: string) => `${word}${DIFF_ID_PREFIX}${id}${DIFF_ID_POSTFIX}${word}`;

    const getPronNameRegex = (pronName: string, plainRegex?: boolean) => {
        const pronNameEscaped = _.escapeRegExp(pronName);
        const attachedIdsWithEachWord = pronNameEscaped
            .split(" ")
            .filter((i) => !!i.length)
            .map((word) => `${word} ${diffIdRegexStr(word)}`)
            .join(" ");

        if (plainRegex) {
            return attachedIdsWithEachWord;
        }

        return new RegExp("(?<=\\s|^|[^\\w])" + attachedIdsWithEachWord + "(?=\\s|$|[^\\w])", "g");
    };

    const getPronsAndDiffIds = (prons: IPron[]) => {
        return prons.map((pron) => {
            const { word } = pron;

            const split = word.split(" ").filter((i) => !!i.length);

            // every grouped item contains the word(0th pos) and the diff ID(1st pos)
            const itemsGrouped: string[] = split.reduce((grouped, current, i) => {
                const cloned = [...grouped];
                const index = i + 1;

                if (index % 2 === 0) {
                    const lastGroupIndex = cloned.length - 1;
                    cloned[lastGroupIndex] = [cloned[lastGroupIndex][0], current];
                } else {
                    cloned.push([current]);
                }

                return cloned;
            }, [] as any[]);

            const actualWord = itemsGrouped.map((i) => i[0]).join(" ");

            const diffIds = itemsGrouped.map((item) => {
                const itemWord = item[0];

                // Id is like this: `${itemWord}${DIFF_ID_PREFIX}${id}${DIFF_ID_POSTFIX}${itemWord}`;
                const rawId = item[1];
                const diffId = rawId.replace(`${itemWord}${DIFF_ID_PREFIX}`, "").replace(`${DIFF_ID_POSTFIX}${itemWord}`, "");

                return { word: itemWord, id: diffId };
            });

            return { pron, actualWord, diffIds };
        });
    };

    const articleTextJoined = collectArticleTextForPronunciationsQa(diff, getDiffId);

    return { articleText: articleTextJoined, getPronNameRegex, getPronsAndDiffIds };
};

export const isAiGenerationType = (article?: IArticle) => ([ArticleNarrationTypes.AI_GOLD, ArticleNarrationTypes.AI_SILVER] as any[]).includes(article?.articleNarrationType);

export const normalizeQaPronWord = (word: string) => {
    return word
        .replaceAll("-", " ")
        .trim()
        .split(" ")
        .filter((i) => !!i.length)
        .join(" ");
};

export const filterPronsFalsePositives = (prons: IQaPron[]) => {
    const ignoreList = ["for", "do"];
    return prons.filter((pron) => {
        const pronWord = pron.word;
        return !ignoreList.includes(pronWord) && pronWord.length > 1;
    });
};

export const removeDuplicatedPronunciations = (prons: IQaPron[]) => {
    /**
     * Removes duplicate pronunciations when a shorter word appears within a longer phrase.
     * This prevents redundant QA checks for the same word when it's part of a longer phrase.
     *
     * Example:
     * If we have pronunciations for "Latif" and "Shams Latif":
     * - "Latif" appears within "Shams Latif"
     * - We'll remove the diffItems from "Latif" that overlap with "Shams Latif"
     * - If "Latif" has no remaining diffItems, it will be removed entirely
     *
     * @param prons - Array of pronunciations to process
     * @returns Filtered array of pronunciations with duplicates removed
     */

    const pronsLength = prons.length;
    const pronsIdsToRemove: string[] = [];

    // Compare each pronunciation against all others
    for (let pronAIndex = 0; pronAIndex < pronsLength; pronAIndex++) {
        const pronA = { ...prons[pronAIndex] };
        const pronAWord = pronA.word;

        // Check if current pronunciation (A) appears within any other pronunciation (B)
        for (let pronBIndex = 0; pronBIndex < pronsLength; pronBIndex++) {
            const pronB = prons[pronBIndex];
            const pronBWord = pronB.word;

            // If B contains A and B is longer than A, remove overlapping diffItems from A
            if (pronBWord.includes(pronAWord) && pronBWord.length > pronAWord.length) {
                // Filter out any diffItems from A that overlap with B's diffItems
                pronA.diffItems = pronA.diffItems.filter((adi) => !pronB.diffItems.some((bdi) => adi.id === bdi.id));
            }
        }

        // If pronunciation A has no remaining diffItems after filtering, mark it for removal
        if (!pronA.diffItems.length) {
            pronsIdsToRemove.push(pronA.qaPronId);
        } else {
            // Update the original array with filtered diffItems
            prons[pronAIndex] = pronA;
        }
    }

    // Remove all pronunciations that were marked for removal
    const pronsFiltered = prons.filter((pron) => !pronsIdsToRemove.includes(pron.qaPronId));

    return pronsFiltered;
};

export const collectBeforeAndAfterWordsForSelectionItem = (diff: IDiffExtendedItemWithGroup[], firstItemId: string, lastItemId: string) => {
    const firstItemIndex = diff.findIndex((di) => di.id === firstItemId);
    const lastItemIndex = diff.findIndex((di) => di.id === lastItemId);

    if (firstItemIndex === -1 || lastItemIndex === -1) return "";

    const numberOfWordsBefore = 20;
    const numberOfWordsAfter = 20;

    const diffLength = diff.length;

    const startPos = firstItemIndex <= numberOfWordsBefore ? 0 : firstItemIndex - numberOfWordsBefore;
    const endPos = lastItemIndex + numberOfWordsAfter >= diffLength - 1 ? diffLength - 1 : lastItemIndex + numberOfWordsAfter;

    // the reason for +1 is that it finds the excluding the end pos
    const diffSliced = diff.slice(startPos, endPos + 1);

    return diffSliced.reduce((joined, current) => `${joined} ${current.reference || ""}`, "");
};
